/**
 * branchFamily — what kind of place a branch is, and which branch it sits inside
 * (branchface-b, 4 Nov 2026).
 *
 * A business runs places inside places: the restaurant inside the hotel, the bakery
 * counter inside the shop. `branches.parent_branch_id` says so, one level deep: a
 * branch inside a branch that is itself inside one is refused by the database
 * (`_branch_family_guard`). A branch inside another bills under the same GSTIN, so it
 * takes its main branch's GSTIN and never has a picker of its own.
 *
 * `branches.kind` is one of a fixed list (a CHECK in the database). Chips, never a
 * free-text box, so "Cafe", "cafe" and "Café" can never be three kinds.
 *
 * Pure: no I/O.
 */

/** The fixed list, in the order the chips are drawn. Must match `branches_kind_chk`. */
export const BRANCH_KINDS = [
    'cafe', 'restaurant', 'hotel', 'bakery', 'shop', 'office', 'warehouse', 'factory', 'clinic', 'other',
] as const;
export type BranchKind = typeof BRANCH_KINDS[number];

const KIND_LABEL: Record<BranchKind, string> = {
    cafe: 'Cafe', restaurant: 'Restaurant', hotel: 'Hotel', bakery: 'Bakery', shop: 'Shop',
    office: 'Office', warehouse: 'Warehouse', factory: 'Factory', clinic: 'Clinic', other: 'Other',
};

export const isBranchKind = (v: unknown): v is BranchKind =>
    typeof v === 'string' && (BRANCH_KINDS as readonly string[]).includes(v);

/** "Restaurant", or null for a kind not said (or one this build does not know). */
export function kindLabel(kind: string | null | undefined): string | null {
    return isBranchKind(kind) ? KIND_LABEL[kind] : null;
}

/** The fields this file reads off a branch row. */
export interface FamilyBranch {
    id: string;
    name: string;
    parent_branch_id?: string | null;
    is_active?: boolean | null;
    is_default?: boolean | null;
}

/** The active branches inside `id`, by name. */
export function childrenOf<T extends FamilyBranch>(id: string, rows: readonly T[]): T[] {
    return rows.filter((b) => b.parent_branch_id === id && b.is_active !== false)
        .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Main branches first, each followed by the branches inside it, so a list draws
 * them indented under their parent. A branch whose parent is not in `rows` (in
 * another card, archived, or out of a scoped member's reach) stands on its own
 * rather than vanishing.
 */
export function familyOrder<T extends FamilyBranch>(rows: readonly T[]): { row: T; depth: 0 | 1 }[] {
    const ids = new Set(rows.map((b) => b.id));
    const tops = rows.filter((b) => !b.parent_branch_id || !ids.has(b.parent_branch_id));
    const out: { row: T; depth: 0 | 1 }[] = [];
    for (const top of tops) {
        out.push({ row: top, depth: 0 });
        for (const child of rows.filter((b) => b.parent_branch_id === top.id)) out.push({ row: child, depth: 1 });
    }
    return out;
}

/**
 * The branches `branchId` may be put inside: active, not itself, not already inside
 * another (one level), and only when `branchId` has nothing inside it of its own.
 * `branchId` null = a branch being added.
 */
export function parentChoices<T extends FamilyBranch>(branchId: string | null, rows: readonly T[]): T[] {
    if (branchId && rows.some((b) => b.parent_branch_id === branchId && b.is_active !== false)) return [];
    return rows.filter((b) => b.id !== branchId && b.is_active !== false && !b.parent_branch_id)
        .sort((a, b) => Number(!!b.is_default) - Number(!!a.is_default) || a.name.localeCompare(b.name));
}

/** "Restaurant and Rooftop Bar", for a sentence naming what was rolled in. */
export function namesSentence(names: readonly string[]): string {
    if (names.length <= 1) return names[0] ?? '';
    return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}
